/**
 * metrics-monetization-adapter.ts — 数据中台商业化 L2-C「真 API DTO → 页面消费形状」塑形
 *
 * GET /api/v1/metrics/monetization 返回 snake_case DTO（服务端 DTO interface = packages/server
 * monetization-metrics-read-service.ts 的 MonetizationData；admin 自有 API，随 P1-4 dev-docs 收录）。
 * 本 adapter 做：
 * - PENDING_INSTRUMENTATION 占位对象 → pending 标记（页面灰显、禁显假数字）；
 *   revenueReady 判据 = financial_coverage.usd_ready（完整版本资格）；
 *   penetration 恒 PENDING（分母 MAU 需 app_open 行为埋点），不进视图数值；
 * - ARPPU minor string → number minor units（页面 fmtUsdMinor 格式化）；
 * - sparks 逐日点 → StatCard spark 数组（null 缺口日剔除，防 0 假谷底）；
 * - funnel 六级混合（value XOR status）→ live/pend 统一行形状（pend 按 key 分 rev/beh 级次）。
 * 契约缺字段落 null / pending，不编造数据。
 */

import { isPendingInstrumentation } from '@novel-admin/shared';

import type { FinancialCoverage } from '@novel-admin/shared/schemas/financial-coverage';

// ---------- DTO（snake_case，镜像服务端 MonetizationData） ----------

type Pending = { status: 'PENDING_INSTRUMENTATION' };

interface SparkPointDto {
  date: string;
  value: number | null;
}

export interface MetricsMonetizationResponse {
  financial_coverage?: FinancialCoverage;
  app_scope: string;
  range: string;
  scope: 'all' | 'app';
  period: { start: string; end: string };
  currency: 'USD';
  generated_at: string;
  data_freshness: { txn: string; behavioral: string };
  kpi: {
    /** 恒 PENDING（分母 MAU 需 app_open 行为埋点） */
    penetration: { value: number | null | Pending; delta_pp: number | null };
    /** live：新用户 7 日内首充转化率（percent，仅成熟 cohort） */
    first_charge_rate: { value: number | null | Pending; delta_pp: number | null };
    /** 收入闸控：净收入 ÷ 付费用户（minor string） */
    arppu: { value: string | null | Pending; currency: 'USD'; delta_pct: number | null };
    /** live：≥2 单付费用户 ÷ 付费用户（percent） */
    repurchase_rate: { value: number | null | Pending; delta_pp: number | null };
  };
  sparks: {
    penetration: SparkPointDto[] | Pending;
    first_charge: SparkPointDto[];
    arppu: SparkPointDto[] | Pending;
    repurchase: SparkPointDto[];
  };
  trend: {
    points: Array<{
      date: string;
      arppu_usd_minor: string | null | Pending;
      penetration: Pending;
    }>;
  };
  funnel: {
    steps: Array<
      | { key: string; label: string; source: string; value: number }
      | { key: string; label: string; source: string; status: Pending }
    >;
    overall_rate: number | null;
  };
}

// ---------- 视图模型（camelCase，页面消费） ----------

export interface MonetizationFunnelStepView {
  key: string;
  label: string;
  /** 数据源标注（行内小字 mono） */
  source: string;
  live: boolean;
  /** live 级次真值；pend 级次 null（页面显 —） */
  value: number | null;
  /** PENDING 级次归因：rev=权威级次待收入 / beh=行为级次待埋点；live → null */
  pend: 'rev' | 'beh' | null;
}

export interface MetricsMonetizationViewData {
  financialCoverage?: FinancialCoverage;
  /** 收入派生是否就绪（false → ARPPU 卡 + 趋势整图 PENDING 灰显） */
  revenueReady: boolean;
  window: { from: string; to: string };
  kpi: {
    /** live：percent（1 位小数）；无成熟 cohort → null */
    firstChargeRate: number | null;
    firstChargeDeltaPp: number | null;
    /** 收入闸控：minor units；未就绪/无付费 → null（区分靠 revenueReady） */
    arppuMinor: number | null;
    arppuDeltaPct: number | null;
    /** live：percent（1 位小数）；无付费 → null */
    repurchaseRate: number | null;
    repurchaseDeltaPp: number | null;
  };
  sparks: {
    /** live：日首充率 %（未成熟日 null 已剔除） */
    firstCharge: number[];
    /** 收入闸控：日 ARPPU minor（未就绪 → []） */
    arppu: number[];
    /** live：日回访买家占比 % */
    repurchase: number[];
  };
  /** ARPPU 30 天趋势（就绪时；xLabels=MM-DD，无付费日 null 断点；未就绪 → 空，页面 PendingBlock） */
  trend: { xLabels: string[]; arppuMinor: Array<number | null> };
  funnel: {
    steps: MonetizationFunnelStepView[];
    /** 新增 → 首充整体转化率（percent，1 位小数）；无新增 → null */
    overallRate: number | null;
  };
}

/** 行为级次 key（analytics_events_raw 埋点源）→ pend='beh'；其余 PENDING 归收入级次 */
const BEHAVIORAL_STEP_KEYS = new Set(['start_reading', 'enter_store']);

/** percent 值 number|null|PENDING → number|null（PENDING → null，页面靠 pending 标记灰显） */
function pctOrNull(v: number | null | Pending | undefined): number | null {
  if (v == null || isPendingInstrumentation(v)) return null;
  return v;
}

/** minor string|null|PENDING → number|null */
function minorOrNull(v: string | null | Pending | undefined): number | null {
  if (v == null || isPendingInstrumentation(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** spark 点列 → 数值数组（PENDING → []；null 缺口日剔除，防 0 假谷底） */
function sparkValues(points: SparkPointDto[] | Pending | undefined): number[] {
  if (!points || isPendingInstrumentation(points)) return [];
  return points.filter((p) => p.value != null).map((p) => p.value as number);
}

export function adaptMetricsMonetization(
  dto: MetricsMonetizationResponse,
): MetricsMonetizationViewData {
  // 收入可用性只能由资格与完整版本共同确认，不从金额或空值反推。
  const revenueReady = dto.financial_coverage?.usd_ready === true;

  const trend: MetricsMonetizationViewData['trend'] = revenueReady
    ? {
        xLabels: dto.trend.points.map((p) => p.date.slice(5)),
        arppuMinor: dto.trend.points.map((p) => minorOrNull(p.arppu_usd_minor)),
      }
    : { xLabels: [], arppuMinor: [] };

  return {
    financialCoverage: dto.financial_coverage,
    revenueReady,
    window: { from: dto.period.start, to: dto.period.end },
    kpi: {
      firstChargeRate: pctOrNull(dto.kpi.first_charge_rate.value),
      firstChargeDeltaPp: dto.kpi.first_charge_rate.delta_pp,
      arppuMinor: revenueReady ? minorOrNull(dto.kpi.arppu.value) : null,
      arppuDeltaPct: revenueReady ? dto.kpi.arppu.delta_pct : null,
      repurchaseRate: pctOrNull(dto.kpi.repurchase_rate.value),
      repurchaseDeltaPp: dto.kpi.repurchase_rate.delta_pp,
    },
    sparks: {
      firstCharge: sparkValues(dto.sparks.first_charge),
      arppu: revenueReady ? sparkValues(dto.sparks.arppu) : [],
      repurchase: sparkValues(dto.sparks.repurchase),
    },
    trend,
    funnel: {
      steps: dto.funnel.steps.map((s) => {
        const live = 'value' in s;
        return {
          key: s.key,
          label: s.label,
          source: s.source,
          live,
          value: live ? s.value : null,
          pend: live ? null : BEHAVIORAL_STEP_KEYS.has(s.key) ? 'beh' : 'rev',
        };
      }),
      overallRate: dto.funnel.overall_rate,
    },
  };
}
